import React from 'react';
import { Box, Container, Typography, Button, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useT } from '../i18n/useT';

const ConnectivityGuide: React.FC = () => {
  const { r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const data = r.connectivityGuide;

  return (
    <Box
      component="section"
      id="connectivity-guide"
      aria-labelledby="connectivity-guide-heading"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'white',
        borderTop: '1px solid #E2E8F0',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
              gap: { xs: 4, md: 6 },
              alignItems: 'stretch',
            }}
          >
            <Box>
              <Typography
                component="span"
                sx={{
                  color: '#64748B',
                  fontWeight: 700,
                  fontSize: '0.8rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  display: 'block',
                  mb: 2,
                }}
              >
                {data.eyebrow}
              </Typography>
              <Typography
                variant="h2"
                id="connectivity-guide-heading"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', md: '2.2rem' },
                  color: '#0F172A',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  mb: 2,
                }}
              >
                {data.title}
              </Typography>
              <Typography
                sx={{
                  color: '#475569',
                  lineHeight: 1.65,
                  fontSize: { xs: '0.97rem', md: '1.05rem' },
                  mb: 4,
                }}
              >
                {data.subtitle}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.options.map((opt) => (
                  <Box
                    key={opt.code}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      gap: 2,
                      alignItems: 'center',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: '#0F172A',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                      aria-hidden
                    >
                      {opt.code}
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontWeight: 700, color: '#0F172A', fontSize: '0.97rem', lineHeight: 1.3 }}
                      >
                        {opt.name}
                      </Typography>
                      <Typography sx={{ color: '#64748B', fontSize: '0.86rem', lineHeight: 1.45, mt: 0.3 }}>
                        {opt.short}
                      </Typography>
                    </Box>
                    <Chip
                      label={opt.isolation}
                      size="small"
                      sx={{
                        display: { xs: 'none', sm: 'inline-flex' },
                        backgroundColor: 'white',
                        color: '#475569',
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        border: '1px solid #E2E8F0',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              <Box
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  backgroundColor: 'rgb(13, 13, 37)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                      'radial-gradient(circle at 80% 20%, rgba(0, 245, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(255, 0, 255, 0.06) 0%, transparent 50%)',
                  },
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PictureAsPdfRoundedIcon sx={{ color: 'white', fontSize: '1.6rem' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'white', lineHeight: 1.2 }}>
                        WAI · Guía de conectividad
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem' }}>
                        {data.downloadMeta}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.92rem', lineHeight: 1.5, mb: 2.5 }}>
                    {data.audience}
                  </Typography>
                  <Button
                    component="a"
                    href={data.downloadHref}
                    download
                    target="_blank"
                    rel="noopener"
                    aria-label={data.downloadAria}
                    startIcon={<DownloadRoundedIcon />}
                    fullWidth
                    sx={{
                      py: 1.3,
                      borderRadius: '50px',
                      backgroundColor: 'white',
                      color: '#0F172A',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.92)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    {data.downloadCta}
                  </Button>
                  <Typography
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.75rem',
                      mt: 2,
                      textAlign: 'center',
                      fontStyle: 'italic',
                    }}
                  >
                    {data.confidential}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LockRoundedIcon sx={{ color: '#0F172A', fontSize: '1.2rem' }} />
                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: 700,
                      color: '#0F172A',
                      fontSize: '0.95rem',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {data.guaranteesTitle}
                  </Typography>
                </Box>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {data.guarantees.map((g) => (
                    <Box
                      component="li"
                      key={g}
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <CheckRoundedIcon sx={{ color: '#0F172A', fontSize: '1.05rem', mt: 0.2, flexShrink: 0 }} />
                      <Typography sx={{ color: '#475569', fontSize: '0.88rem', lineHeight: 1.5 }}>
                        {g}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ConnectivityGuide;
